// src/admin/pages/KnowledgeBasePage.jsx
// Superadmin console → Knowledge Base. Route /admin/knowledge-base.
// SAs have no implicit tenant → tenant dropdown (mirrors RunListPage), URL-synced,
// last-used tenant remembered per-user in localStorage.
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { adminListTenants } from '../adminApi.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import KnowledgeBaseView from '../../context/KnowledgeBaseView.jsx';
import { PageHeader, PAGE_COLUMN } from '../../components/ui/SectionCard.jsx';
import { COLORS, FONT_SANS, RADII } from '../../constants/theme.js';

const FONT = FONT_SANS;

export default function KnowledgeBasePage() {
  const { t } = useTranslation('admin');
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTenant = searchParams.get('tenant') || '';
  const [tenants, setTenants] = useState([]);

  const tenantStorageKey = `kb_last_tenant:${user?.user_id || user?.email || 'anon'}`;

  const patchParams = useCallback((patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === '' || v == null) next.delete(k);
      else next.set(k, String(v));
    });
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    adminListTenants().then(setTenants).catch(() => setTenants([]));
  }, []);

  useEffect(() => {
    if (urlTenant) {
      try { localStorage.setItem(tenantStorageKey, urlTenant); } catch {}
      return;
    }
    try {
      const last = localStorage.getItem(tenantStorageKey);
      if (last) patchParams({ tenant: last });
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTenant, tenantStorageKey]);

  return (
    // 2026-09-27: the Gestione Workspace look — PAGE_COLUMN + PageHeader, the
    // tenant dropdown on the header's right; the cards come from the body.
    <div style={PAGE_COLUMN}>
      <PageHeader
        title={t('knowledgeBase.title')}
        subtitle={t('knowledgeBase.subtitle')}
        action={(
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: COLORS.textSecondary }}>
            {t('shared.tenantLabel')}
            <select
              value={urlTenant}
              onChange={e => patchParams({ tenant: e.target.value })}
              style={{
                height: '36px', padding: '0 10px', borderRadius: RADII.control,
                border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                fontSize: '13px', fontFamily: FONT, color: COLORS.ink, minWidth: '240px',
              }}
            >
              <option value="">{t('shared.selectPlaceholder')}</option>
              {tenants.map(tn => (
                <option key={tn.id} value={tn.id}>{tn.display_name || tn.id}</option>
              ))}
            </select>
          </label>
        )}
      />

      {urlTenant ? (
        <KnowledgeBaseView tenantId={urlTenant} canWrite userKey={user?.user_id || user?.email || ''} />
      ) : (
        <div style={{
            padding: '60px 40px', textAlign: 'center', background: COLORS.surface,
            borderRadius: RADII.control, border: `1px solid ${COLORS.border}`,
            color: COLORS.textSecondary, fontSize: '13px',
          }}>
            {t('knowledgeBase.selectTenantPrompt')}
          </div>
      )}
    </div>
  );
}
